/* eslint-disable @typescript-eslint/ban-ts-comment */
import { EventAttachment, RawEventMessage } from '@weblite/ts-action/message'
import { CallType } from '@weblite/ts-action/media-proxy'
import { getRoom } from '$logic/room'
import {
  formatCallDurationVerbally,
  formatScheduledCallEvent,
  getCallScheduledEventTokens,
} from '$logic/call'
import {
  NoUserEventAttachment,
  SingleUserEventAttachment,
  DoubleUserEventAttachment,
} from '$logic/message/react-hook'
import { useAsyncFunction } from '$func/async-function.react-hook'
import {
  TextToken,
  UserToken,
  getTextToken,
  MessageToken,
} from './react-hook.helper'
import { t } from '$i18n'
import { HallCallType } from '$comp/modals/create-hall-call/choose-call-time'

type EventToken = TextToken | UserToken | MessageToken

const getNoUserEventText = (
  // @ts-ignore
  { event, name, messageIid, ...eventAttachment }: NoUserEventAttachment,
  roomIsGroup: boolean,
): EventToken[] => {
  return event === 'PIN_MESSAGE'
    ? t('chat.event.pinnedMessage')(null, messageIid)
    : [
        getTextToken(
          event === 'CALL_STARTED'
            ? // @ts-ignore
              (eventAttachment.callType as CallType) === 'live'
              ? t('chat.liveStarted')
              : // @ts-ignore
              (eventAttachment.callType as CallType) === 'tribune'
              ? t('chat.tribuneStarted')
              : ''
            : event === 'CALL_ENDED'
            ? // @ts-ignore
              (eventAttachment.callType as CallType) === 'group_call'
              ? `${t('chat.meetingEnded')} (${formatCallDurationVerbally(
                  (eventAttachment as any).duration as number,
                )})`
              : // @ts-ignore
              (eventAttachment.callType as CallType) === 'live'
              ? `${t('chat.liveEnded')} (${formatCallDurationVerbally(
                  (eventAttachment as any).duration as number,
                )})`
              : // @ts-ignore
              (eventAttachment.callType as CallType) === 'tribune'
              ? `${t('chat.tribuneEnded')} (${formatCallDurationVerbally(
                  (eventAttachment as any).duration as number,
                )})`
              : ''
            : event === 'CREATE_HALL'
            ? // @ts-ignore
              `${roomIsGroup ? t('common.group') : t('common.channel')} ${t(
                'chat.createHall',
              )}`
            : event === 'CHANGE_HALL_IMAGE'
            ? // @ts-ignore
              !eventAttachment.image
              ? // @ts-ignore
                t('chat.removeHallImage')(roomIsGroup)
              : // @ts-ignore
                t('chat.changeHallImage')(roomIsGroup)
            : event === 'CHANGE_HALL_NAME'
            ? // @ts-ignore
              t('chat.changeHallName')(roomIsGroup, name)
            : event === 'CALL_SCHEDULED'
            ? formatScheduledCallEvent(
                (eventAttachment as any).startingAt,
                (eventAttachment as any).callType,
              )
            : null,
        ),
      ]
}

const getSingleUserEventTokensMap = (userId: string, messageIid: string) => ({
  MEETING_STARTED: t('chat.event.meetingStarted')(userId),
  JOIN_GROUP_VIA_LINK: t('chat.event.joinedGroupViaLink')(userId),
  JOIN_GROUP: t('chat.event.joinedGroup')(userId),
  LEAVE_GROUP: t('chat.event.leaveGroup')(userId),
  SIGNUP_CONTACT: t('chat.event.signUpContact')(userId),
  PIN_MESSAGE: t('chat.event.pinnedMessage')(userId, messageIid),

  CALL_STARTED__group_call: t('chat.event.meetingStarted')(userId),
  CALL_STARTED__live: t('chat.event.liveStarted')(),
  CALL_STARTED__tribune: t('chat.event.tribuneStarted')(),
})

const getSingleUserEventText = (
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  {
    userId,
    event,
    messageIid,
    callType,
    ...attachment
  }: SingleUserEventAttachment,
): EventToken[] =>
  event === 'CALL_SCHEDULED'
    ? getCallScheduledEventTokens(
        (attachment as any).startingAt,
        callType as HallCallType,
        userId,
      )
    : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      getSingleUserEventTokensMap(userId, messageIid)[
        // @ts-ignore
        event === 'CALL_STARTED' ? `CALL_STARTED__${callType}` : event
      ]

const getDoubleUserEventTokensMap = (
  doerUserId: string,
  affectedUserId: string,
  isGroup: boolean,
) => ({
  ADD_TO_GROUP: t('chat.event.addToGroup')(doerUserId, affectedUserId, isGroup),
  REMOVE_FROM_GROUP: t('chat.event.removeFromGroup')(
    doerUserId,
    affectedUserId,
    isGroup,
  ),
})

const getDoubleUserEventText = (
  { event, doerUserId, affectedUserId }: DoubleUserEventAttachment,
  isGroup: boolean,
): EventToken[] =>
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  getDoubleUserEventTokensMap(doerUserId, affectedUserId, isGroup)[event]

const getEventTokens = async ({
  message,
  isGroup,
  roomId,
}: {
  message: RawEventMessage
  isGroup?: boolean
  roomId?: string
}): Promise<EventToken[]> => {
  const eventAttachment = message.attachment as EventAttachment

  const tokens = (eventAttachment as SingleUserEventAttachment).userId
    ? getSingleUserEventText(eventAttachment as SingleUserEventAttachment)
    : (eventAttachment as DoubleUserEventAttachment).doerUserId
    ? getDoubleUserEventText(
        eventAttachment as DoubleUserEventAttachment,
        isGroup !== null ? isGroup : (await getRoom(roomId))?.type === 'GROUP',
      )
    : getNoUserEventText(
        eventAttachment as NoUserEventAttachment,
        isGroup !== null ? isGroup : (await getRoom(roomId))?.type === 'GROUP',
      )

  return tokens?.filter(Boolean)
}

export function useEventTokens({
  message,
  isGroup = null,
  roomId = null,
}: {
  message: RawEventMessage
  isGroup?: boolean
  roomId?: string
}): { eventTokens: EventToken[]; loading: boolean } {
  const { value: eventTokens, loading } = useAsyncFunction<EventToken[]>(
    () => getEventTokens({ message, isGroup, roomId }),
    [roomId, message],
  )

  return { eventTokens, loading }
}
